import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  TestCase,
  TestCasesResponse,
  CreateTestCaseRequest,
  UpdateTestCaseRequest,
  TestStep,
  CreateTestStepRequest,
  UpdateTestStepRequest,
} from '@/types/testcase.types'

export const testCaseKeys = {
  all: ['testcases'] as const,
  lists: () => [...testCaseKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...testCaseKeys.lists(), filters] as const,
  details: () => [...testCaseKeys.all, 'detail'] as const,
  detail: (id: number) => [...testCaseKeys.details(), id] as const,
  steps: (id: number) => [...testCaseKeys.all, 'steps', id] as const,
}

export const useTestCases = (params?: {
  project_id?: number
  parent_id?: number
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: testCaseKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.project_id) queryParams.append('project_id', params.project_id.toString())
      if (params?.parent_id) queryParams.append('parent_id', params.parent_id.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())

      const { data } = await apiClient.get<TestCasesResponse>(
        `${API_CONFIG.ENDPOINTS.TEST_CASES.LIST}?${queryParams.toString()}`
      )
      return data.testcases
    },
  })
}

export const useTestCasesWithMeta = (params?: {
  project_id?: number
  parent_id?: number
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: testCaseKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.project_id) queryParams.append('project_id', params.project_id.toString())
      if (params?.parent_id) queryParams.append('parent_id', params.parent_id.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())

      const { data } = await apiClient.get<TestCasesResponse>(
        `${API_CONFIG.ENDPOINTS.TEST_CASES.LIST}?${queryParams.toString()}`
      )
      return data
    },
  })
}

export const useTestCase = (id: number) => {
  return useQuery({
    queryKey: testCaseKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<TestCase>(
        API_CONFIG.ENDPOINTS.TEST_CASES.GET(id)
      )
      return data
    },
    enabled: !!id,
  })
}

export const useTestCaseSteps = (testcaseId: number) => {
  return useQuery({
    queryKey: testCaseKeys.steps(testcaseId),
    queryFn: async () => {
      const { data } = await apiClient.get<{ steps: TestStep[] }>(
        API_CONFIG.ENDPOINTS.TEST_CASES.STEPS(testcaseId)
      )
      return data.steps
    },
    enabled: !!testcaseId,
  })
}

export const useCreateTestCase = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (testcaseData: CreateTestCaseRequest) => {
      const { data } = await apiClient.post<TestCase>(
        API_CONFIG.ENDPOINTS.TEST_CASES.CREATE,
        testcaseData
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: testCaseKeys.lists() })
    },
  })
}

export const useUpdateTestCase = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
      updaterId,
    }: {
      id: number
      data: UpdateTestCaseRequest
      updaterId?: number
    }) => {
      const payload = updaterId ? { ...updateData, updater_id: updaterId } : updateData
      const { data } = await apiClient.put<TestCase>(
        API_CONFIG.ENDPOINTS.TEST_CASES.UPDATE(id),
        payload
      )
      return data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: testCaseKeys.detail(variables.id) })
      qc.invalidateQueries({ queryKey: testCaseKeys.lists() })
    },
  })
}

export const useDeleteTestCase = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API_CONFIG.ENDPOINTS.TEST_CASES.DELETE(id))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: testCaseKeys.lists() })
    },
  })
}

export const useCreateTestStep = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      testcaseId,
      stepData,
    }: {
      testcaseId: number
      stepData: CreateTestStepRequest
    }) => {
      const { data } = await apiClient.post<TestStep>(
        API_CONFIG.ENDPOINTS.TEST_CASES.STEPS(testcaseId),
        stepData
      )
      return data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: testCaseKeys.steps(variables.testcaseId) })
      qc.invalidateQueries({ queryKey: testCaseKeys.detail(variables.testcaseId) })
    },
  })
}

export const useUpdateTestStep = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      testcaseId,
      stepId,
      stepData,
    }: {
      testcaseId: number
      stepId: number
      stepData: UpdateTestStepRequest
    }) => {
      const { data } = await apiClient.put<TestStep>(
        `${API_CONFIG.ENDPOINTS.TEST_CASES.STEPS(testcaseId)}/${stepId}`,
        stepData
      )
      return data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: testCaseKeys.steps(variables.testcaseId) })
    },
  })
}

export const useDeleteTestStep = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      testcaseId,
      stepId,
    }: {
      testcaseId: number
      stepId: number
    }) => {
      await apiClient.delete(
        `${API_CONFIG.ENDPOINTS.TEST_CASES.STEPS(testcaseId)}/${stepId}`
      )
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: testCaseKeys.steps(variables.testcaseId) })
      qc.invalidateQueries({ queryKey: testCaseKeys.detail(variables.testcaseId) })
    },
  })
}
